import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useFavorites } from "../../contexts/FavoritesContext";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, Alert, Skeleton, Stack, Button, Chip, Grid,
} from "@mui/material";
import {
  LockOutlined, FavoriteBorder, PlayCircleOutline,
  TrendingUpOutlined, ArrowForward, AutoAwesome,
  OndemandVideo, EmojiEvents,
} from "@mui/icons-material";
import {
  ContentContainer, StyledTabs, StyledTab, MEMBERSHIP_COLORS,
  fadeSlideUp, scaleIn, shimmerFlow,
} from "../../components/membership/MembershipStyles";
import VideoRow from "../../components/membership/VideoRow";
import videosService, { Video } from "../../services/videosService";
import { keyframes } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";

const BLUE       = MEMBERSHIP_COLORS.blue;
const BLUE_LIGHT = MEMBERSHIP_COLORS.blueLight;
const NAVY       = MEMBERSHIP_COLORS.navy;
const WHITE      = MEMBERSHIP_COLORS.white;
const BG         = MEMBERSHIP_COLORS.bg;
const BORDER     = MEMBERSHIP_COLORS.border;
const MUTED      = MEMBERSHIP_COLORS.muted;

const floatY = keyframes`
  0%,100%{ transform:translateY(0) rotate(0deg); }
  50%    { transform:translateY(-14px) rotate(5deg); }
`;
const morphBg = keyframes`
  0%,100%{ border-radius:62% 38% 46% 54%/60% 44% 56% 40%; }
  50%    { border-radius:30% 70% 60% 40%/40% 62% 38% 60%; }
`;
const countUp = keyframes`
  from { transform:translateY(8px); opacity:0; }
  to   { transform:translateY(0);   opacity:1; }
`;

const formatDuration = (s?: number) => {
  if (!s || s <= 0) return "Quick Start";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

// Group videos by whatever categories actually exist in the data, in the
// order they first appear — no hard-coded category list.
const buildRows = (videos: any[]) => {
  const map: Record<string, any[]> = {};
  const order: string[] = [];
  videos.forEach(v => {
    const key = v.type || "Videos";
    if (!map[key]) { map[key] = []; order.push(key); }
    map[key].push(v);
  });
  return order.map(c => ({ title: c, videos: map[c] }));
};

const toRowVideo = (v: Video) => ({
  id: v._id,
  title: v.title,
  duration: formatDuration(v.duration),
  type: v.category || v.tags?.[0] || "",
  thumbnail: v.thumbnailUrl || "https://images.unsplash.com/photo-1628009368231-7bb7cfcb0def?w=600&auto=format&fit=crop",
  isLocked: v.isAccessible === false,
});

const TIER_LABELS: Record<string, string> = {
  "pre-recorded": "Pre-Recorded", monthly: "Monthly", quarterly: "Quarterly",
  annual: "Annual", basic: "Basic", premium: "Premium",
};

// quickLinks is built inside the component to use translations.
// The favorites link opens the Favorites tab on this page (tab index 1)
// instead of a standalone route.
const QUICK_LINKS_CONFIG: { icon: string; path?: string; tab?: number; color: string }[] = [
  { icon: "🎓", path: "/membership/foundation", color: BLUE },
  { icon: "🎯", path: "/membership/coaching",   color: "#7B1FA2" },
  { icon: "📚", path: "/membership/classes",    color: "#00897B" },
  { icon: "❤️", tab: 1,                          color: "#E53935" },
];

const Dashboard: React.FC = () => {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const { t }      = useLanguage();
  const td         = t.dashboard;
  const [activeTab, setActiveTab] = useState(0);
  const { favoritedIds } = useFavorites();
  const [videos,   setVideos]   = useState<Video[]>([]);
  const [userTier, setUserTier] = useState<string>("free");
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [mounted,  setMounted]  = useState(false);

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError(null);
        const r = await videosService.getVideos({ limit: 200 });
        setVideos(r.videos || []);
        setUserTier(r.userTier || "free");
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load library videos");
      } finally { setLoading(false); }
    })();
  }, []);

  const handleTabChange = useCallback((_: any, v: number) => setActiveTab(v), []);

  const allRows       = useMemo(() => buildRows(videos.map(toRowVideo)), [videos]);
  const advancedRows  = useMemo(() => buildRows(videos.filter(v => (v.level || "") === "advanced").map(toRowVideo)), [videos]);
  const favoriteRows  = useMemo(() => buildRows(videos.filter(v => favoritedIds.has(v._id)).map(toRowVideo)), [videos, favoritedIds]);
  const currentContent = activeTab === 0 ? advancedRows : favoriteRows;

  const totalVideos    = videos.length;
  const lockedVideos   = videos.filter(v => v.isAccessible === false).length;
  const unlockedVideos = totalVideos - lockedVideos;
  const favCount       = favoritedIds.size;

  const quickLinks = [
    { label: td.foundation, desc: td.foundationDesc, ...QUICK_LINKS_CONFIG[0] },
    { label: td.coaching,   desc: td.coachingDesc,   ...QUICK_LINKS_CONFIG[1] },
    { label: td.classes,    desc: td.classesDesc,    ...QUICK_LINKS_CONFIG[2] },
    { label: td.favorites,  desc: td.favoritesDesc,  ...QUICK_LINKS_CONFIG[3] },
  ];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? td.goodMorning : hour < 18 ? td.goodAfternoon : td.goodEvening;

  const statCards = [
    { icon: <OndemandVideo sx={{ fontSize: 22 }} />, value: totalVideos,     label: td.totalVideos, color: BLUE,      bg: "rgba(21,101,192,0.1)"  },
    { icon: <PlayCircleOutline sx={{ fontSize: 22 }} />, value: unlockedVideos, label: td.unlocked,  color: "#00897B", bg: "rgba(0,137,123,0.1)"   },
    { icon: <FavoriteBorder sx={{ fontSize: 22 }} />,   value: favCount,       label: td.favorites,  color: "#E53935", bg: "rgba(229,57,53,0.1)"   },
    { icon: <EmojiEvents sx={{ fontSize: 22 }} />,      value: allRows.length, label: td.categories, color: "#7B1FA2", bg: "rgba(123,31,162,0.1)"  },
  ];

  return (
    <Box sx={{ backgroundColor: BG, minHeight: "100vh" }}>

      {/* ─── Hero Banner ─────────────────────────────────────── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #0D2137 55%, #0A2040 100%)`,
        position: "relative", overflow: "hidden",
        pt: { xs: 5, md: 8 }, pb: { xs: 8, md: 12 },
      }}>
        {/* Grid overlay */}
        <Box sx={{
          position: "absolute", inset: 0, zIndex: 0, opacity: .04,
          backgroundImage: `linear-gradient(rgba(255,255,255,.8) 1px,transparent 1px),
                            linear-gradient(90deg,rgba(255,255,255,.8) 1px,transparent 1px)`,
          backgroundSize: "48px 48px",
        }} />

        {/* Orbs */}
        {[
          { top: "-20%", right: "-5%", w: 320, dur: "16s" },
          { top: "50%",  left: "-8%", w: 250,  dur: "20s" },
        ].map((o, i) => (
          <Box key={i} sx={{
            position: "absolute", top: o.top, right: o.right, left: o.left,
            width: o.w, height: o.w, zIndex: 0,
            background: `radial-gradient(circle,rgba(21,101,192,.2) 0%,transparent 70%)`,
            animation: `${morphBg} ${o.dur} ease-in-out infinite ${i % 2 ? "reverse" : ""}`,
          }} />
        ))}

        {/* Floating icons */}
        {["🐾", "⭐", "🎓", "❤️"].map((icon, i) => (
          <Box key={i} sx={{
            position: "absolute", zIndex: 0, fontSize: { xs: "1.4rem", md: "1.8rem" },
            opacity: .1, pointerEvents: "none",
            top: `${15 + i * 18}%`, left: i % 2 === 0 ? `${4 + i * 2}%` : "auto",
            right: i % 2 !== 0 ? `${4 + i * 3}%` : "auto",
            animation: `${floatY} ${7 + i * 2}s ease-in-out infinite`,
            animationDelay: `${i * 1.5}s`,
          }}>{icon}</Box>
        ))}

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateY(0)" : "translateY(24px)",
            transition: "all .7s cubic-bezier(.22,1,.36,1) .1s",
          }}>
            {/* Online badge */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              backgroundColor: "rgba(66,165,245,.15)", border: "1px solid rgba(66,165,245,.3)",
              borderRadius: "50px", px: 2.5, py: .8, mb: 3,
            }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%", backgroundColor: "#4CAF50",
                boxShadow: "0 0 8px #4CAF50",
                animation: "gpulse 1.8s ease-in-out infinite",
                "@keyframes gpulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: .5 } },
              }} />
              <Typography sx={{ color: BLUE_LIGHT, fontWeight: 700, fontSize: ".8rem", letterSpacing: ".1em" }}>
                {td.welcomeBack}
              </Typography>
            </Box>

            {/* Greeting */}
            <Typography sx={{
              fontSize: { xs: "2rem", md: "3rem" }, fontWeight: 800, color: WHITE,
              letterSpacing: "-.03em", lineHeight: 1.12, mb: 1.5,
            }}>
              {greeting},{" "}
              <Box component="span" sx={{
                background: `linear-gradient(135deg,${BLUE_LIGHT},#90CAF9)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                {user?.name?.split(" ")[0] || "Member"} 👋
              </Box>
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.65)", fontSize: "1rem", mb: 5, maxWidth: 500 }}>
              {td.subtitle}
            </Typography>

            {/* Quick links */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {quickLinks.map((ql, i) => (
                <Box
                  key={i}
                  onClick={() => (ql.path ? navigate(ql.path) : setActiveTab(ql.tab ?? 0))}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    backgroundColor: "rgba(255,255,255,.08)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,255,255,.12)",
                    borderRadius: "16px", px: 2.5, py: 1.5,
                    cursor: "pointer",
                    transition: "all .3s cubic-bezier(.34,1.56,.64,1)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,.15)", transform: "translateY(-3px) scale(1.03)",
                      borderColor: `${ql.color}40`,
                      boxShadow: `0 8px 24px ${ql.color}30`,
                    },
                    opacity: mounted ? 1 : 0,
                    transform: mounted ? "translateY(0)" : "translateY(16px)",
                    transitionDelay: `${.3 + i * .08}s`,
                  }}
                >
                  <Box sx={{ fontSize: "1.2rem" }}>{ql.icon}</Box>
                  <Box>
                    <Typography sx={{ color: WHITE, fontWeight: 700, fontSize: ".85rem", lineHeight: 1.2 }}>{ql.label}</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: ".7rem" }}>{ql.desc}</Typography>
                  </Box>
                  <ArrowForward sx={{ color: "rgba(255,255,255,.3)", fontSize: "1rem", ml: .5 }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Container>

        {/* Bottom wave */}
        <Box sx={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0, zIndex: 2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: "100%", height: "50px", display: "block" }}>
            <path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill={BG} />
          </svg>
        </Box>
      </Box>

      {/* ─── Stats Cards ─────────────────────────────────────── */}
      <Container maxWidth="xl" sx={{ mt: { xs: -3, md: -5 }, position: "relative", zIndex: 3, mb: 2 }}>
        <Grid container spacing={{ xs: 1.5, md: 2 }}>
          {statCards.map((s, i) => (
            <Grid key={i} size={{ xs: 6, md: 3 }}>
              <Box sx={{
                bgcolor: WHITE,
                borderRadius: "20px",
                border: `1px solid ${BORDER}`,
                p: { xs: 2, md: 2.5 },
                display: "flex", alignItems: "center", gap: 2,
                boxShadow: "0 4px 20px rgba(21,101,192,0.08)",
                opacity: mounted ? 1 : 0,
                transform: mounted ? "translateY(0)" : "translateY(20px)",
                transition: `all .6s cubic-bezier(.22,1,.36,1) ${.4 + i * .1}s`,
                "&:hover": {
                  boxShadow: "0 8px 32px rgba(21,101,192,0.14)",
                  transform: "translateY(-2px)",
                },
              }}>
                <Box sx={{
                  width: 46, height: 46, borderRadius: "14px",
                  bgcolor: s.bg, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  color: s.color, flexShrink: 0,
                }}>
                  {s.icon}
                </Box>
                <Box>
                  <Typography sx={{
                    fontWeight: 800, fontSize: { xs: "1.3rem", md: "1.6rem" },
                    color: NAVY, lineHeight: 1,
                    animation: mounted ? `${countUp} .5s ease ${.5 + i * .1}s both` : "none",
                  }}>
                    {loading ? <Skeleton width={40} sx={{ bgcolor: "rgba(21,101,192,0.06)" }} /> : s.value}
                  </Typography>
                  <Typography sx={{ fontSize: ".72rem", color: MUTED, fontWeight: 600, mt: .3 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ─── Tabs ────────────────────────────────────────────── */}
      <Box sx={{
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: WHITE,
        position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 2px 12px rgba(21,101,192,0.06)",
      }}>
        <Container maxWidth="xl">
          <StyledTabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <StyledTab label={td.advancedLibrary} icon={<TrendingUpOutlined sx={{ fontSize: "1rem" }} />} iconPosition="start" />
            <StyledTab label={td.favorites}        icon={<FavoriteBorder sx={{ fontSize: "1rem" }} />}    iconPosition="start" />
          </StyledTabs>
        </Container>
      </Box>

      <ContentContainer maxWidth="xl">

        {/* Plan banner */}
        {!loading && (
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 1, mb: 4,
            px: 3, py: 2, borderRadius: "16px",
            background: userTier === "free"
              ? `linear-gradient(135deg, rgba(21,101,192,0.05), rgba(21,101,192,0.1))`
              : `linear-gradient(135deg, rgba(21,101,192,0.08), rgba(66,165,245,0.12))`,
            border: `1px solid ${BORDER}`,
            animation: mounted ? `${fadeSlideUp} .6s ease both` : "none",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px",
                bgcolor: userTier === "free" ? "rgba(21,101,192,0.12)" : "rgba(21,101,192,0.18)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {userTier === "free"
                  ? <LockOutlined sx={{ fontSize: "1.1rem", color: MUTED }} />
                  : <AutoAwesome sx={{ fontSize: "1.1rem", color: BLUE }} />
                }
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: ".9rem", color: NAVY }}>
                  {userTier === "free"
                    ? td.freePlan
                    : `${TIER_LABELS[userTier] || userTier} ${td.activePlan}`
                  }
                </Typography>
                <Typography sx={{ fontSize: ".75rem", color: MUTED }}>
                  {userTier === "free"
                    ? "Upgrade to unlock all premium videos and courses."
                    : "Enjoy your full access to all content."
                  }
                </Typography>
              </Box>
            </Box>
            {userTier !== "premium" && (
              <Button
                size="small" variant="contained"
                onClick={() => navigate("/#pricing")}
                sx={{
                  textTransform: "none", fontWeight: 700, fontSize: ".82rem",
                  backgroundColor: BLUE, borderRadius: "20px", px: 2.5, py: 1,
                  boxShadow: `0 4px 14px rgba(21,101,192,.3)`,
                  "&:hover": { backgroundColor: NAVY },
                }}
                endIcon={<ArrowForward sx={{ fontSize: ".9rem" }} />}
              >
                {td.upgradePlan}
              </Button>
            )}
          </Box>
        )}

        {/* Content */}
        <Box sx={{ mt: 2, opacity: mounted ? 1 : 0, transition: "opacity .5s ease .3s" }}>
          <Box sx={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", mb: { xs: 3, md: 4 }, flexWrap: "wrap", gap: 1,
          }}>
            <Box>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: "1.2rem", md: "1.5rem" },
                color: NAVY, letterSpacing: "-.02em",
              }}>
                {activeTab === 0 ? td.advancedLibrary : td.myFavorites}
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: ".82rem", mt: .3 }}>
                {activeTab === 0
                  ? "Premium content organized by category"
                  : "Videos you've saved for quick access"
                }
              </Typography>
            </Box>
            <Chip
              label={`${currentContent.reduce((a, r) => a + r.videos.length, 0)} videos`}
              size="small"
              sx={{ backgroundColor: "rgba(21,101,192,.1)", color: BLUE, fontWeight: 700, border: `1px solid ${BORDER}` }}
            />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: "14px" }}>{error}</Alert>}

          {loading ? (
            <Stack spacing={3}>
              {[1, 2, 3].map(i => (
                <Box key={i}>
                  <Skeleton variant="text" width={180} height={28} sx={{ mb: 1.5, borderRadius: "8px" }} />
                  <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
                    {[1, 2, 3, 4].map(j => (
                      <Skeleton key={j} variant="rounded" width={280} height={170}
                        sx={{
                          borderRadius: "14px", flexShrink: 0,
                          background: `linear-gradient(90deg,rgba(21,101,192,.06) 25%,rgba(21,101,192,.12) 50%,rgba(21,101,192,.06) 75%)`,
                          backgroundSize: "800px 100%",
                          animation: `${shimmerFlow} 1.8s infinite`,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : currentContent.length === 0 ? (
            <Box sx={{
              textAlign: "center", py: 10,
              backgroundColor: WHITE, borderRadius: "24px",
              border: `1px solid ${BORDER}`,
              animation: `${scaleIn} .5s ease both`,
            }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: "50%",
                background: `linear-gradient(135deg, ${BLUE}, #42A5F5)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                mx: "auto", mb: 2.5, fontSize: "1.8rem",
              }}>
                {activeTab === 1 ? "❤️" : "📚"}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: NAVY, mb: 1 }}>
                {activeTab === 1 ? td.noFavorites : td.noVideos}
              </Typography>
              <Typography sx={{ color: MUTED, mb: 3, maxWidth: 360, mx: "auto" }}>
                {activeTab === 1
                  ? "Press the ❤️ on any video to save it here."
                  : "Check back soon — new content is added regularly."
                }
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/membership/classes")}
                sx={{
                  textTransform: "none", fontWeight: 700, borderRadius: "50px",
                  bgcolor: BLUE, px: 3, py: 1,
                  boxShadow: "0 4px 14px rgba(21,101,192,0.3)",
                  "&:hover": { bgcolor: NAVY },
                }}
              >
                {td.browseClasses}
              </Button>
            </Box>
          ) : (
            currentContent.map((category, i) => (
              <Box key={category.title} sx={{
                animation: `${fadeSlideUp} .6s ease both`,
                animationDelay: `${i * .08}s`,
              }}>
                <VideoRow title={category.title} videos={category.videos} />
              </Box>
            ))
          )}
        </Box>

      </ContentContainer>
    </Box>
  );
};

export default Dashboard;
